import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  HiOutlineArrowRight,
  HiOutlineClock,
  HiOutlineLockClosed,
  HiOutlineSquare3Stack3D,
} from 'react-icons/hi2';

// UI copy keyed by pack size. Packs themselves come from the backend catalog
// (cloud/backend/src/config/plans.js → CREDIT_TOPUP_OPTIONS); a pack with no
// entry here renders without a badge, so adding a pack never breaks the grid.
const TOPUP_ANNOTATIONS = {
  100000: { label: 'Popular' },
  1000000: { label: 'Best Value', featured: true },
};

const TRUST_CHIPS = [
  { icon: HiOutlineClock, text: 'Valid for 12 months' },
  { icon: HiOutlineSquare3Stack3D, text: 'Stacks with your plan' },
  { icon: HiOutlineLockClosed, text: 'Secured by Stripe' },
];

const FREE_PLAN_TOOLTIP = 'Subscribe to a plan to unlock credit top-ups.';
const UNAVAILABLE_TOOLTIP = 'This pack is not available yet.';
const PLANS_PATHS = ['/plans', '/plans-legacy'];

const formatNum = (n) => (n || 0).toLocaleString();

// Price per 1,000 credits — the number that makes the bigger packs make sense.
const ratePerThousand = (topUp) => (topUp.credits > 0 ? topUp.price / (topUp.credits / 1000) : null);

// Four rising bars: a glanceable "how big is this pack" cue.
const PackMeter = ({ level, total, featured }) => (
  <div className="flex items-end gap-[3px] h-4" aria-hidden="true">
    {Array.from({ length: total }).map((_, i) => (
      <span
        key={i}
        className={`w-1.5 rounded-sm transition-colors ${
          i < level
            ? featured
              ? 'bg-gradient-to-t from-blue-500 via-cyan-500 to-purple-500'
              : 'bg-gradient-to-t from-blue-400 to-cyan-400'
            : 'bg-gray-200 dark:bg-white/10'
        }`}
        style={{ height: `${((i + 1) / total) * 100}%` }}
      />
    ))}
  </div>
);

const TopUpCard = ({
  topUp,
  level,
  total,
  baseRate,
  currentBalance,
  isFreePlan,
  disabled,
  disabledTooltip,
  actionLoading,
  onBuyCredits,
}) => {
  const annotation = TOPUP_ANNOTATIONS[topUp.credits] || {};
  const featured = Boolean(annotation.featured);
  const rate = ratePerThousand(topUp);
  const savings = rate != null && baseRate ? Math.round((1 - rate / baseRate) * 100) : 0;
  const isLoading = actionLoading === `credits-${topUp.stripe_price_id}`;
  const unavailable = !topUp.stripe_price_id;
  const isDisabled = disabled || unavailable || isFreePlan || isLoading;
  const tooltip = disabled
    ? disabledTooltip
    : isFreePlan
      ? FREE_PLAN_TOOLTIP
      : unavailable
        ? UNAVAILABLE_TOOLTIP
        : undefined;
  const newBalance = typeof currentBalance === 'number' ? currentBalance + topUp.credits : null;

  return (
    <div
      className={`group relative flex flex-col rounded-2xl p-5 md:p-6 transition-all duration-300 hover:-translate-y-1 ${
        featured
          ? 'border-2 border-cyan-400/50 dark:border-cyan-400/40 bg-gradient-to-br from-cyan-400/[0.08] via-blue-500/[0.04] to-purple-500/[0.04] dark:from-cyan-400/[0.06] dark:via-blue-500/[0.03] dark:to-gray-900/80 shadow-glow-cyan hover:shadow-glow-md'
          : 'border border-gray-200 dark:border-gray-700/40 bg-white dark:bg-gray-900/60 hover:border-gray-300 dark:hover:border-gray-600/60 hover:shadow-card-hover dark:hover:shadow-card-hover-dark'
      }`}
    >
      {/* Ambient glow for the featured pack */}
      {featured && (
        <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-blue-500 via-cyan-500 to-purple-500 opacity-[0.06] blur-xl pointer-events-none" />
      )}

      {/* Badge */}
      {annotation.label && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2 z-20">
          <span className={`px-3 py-1 text-[10px] font-bold uppercase tracking-wider rounded-full whitespace-nowrap shadow-md ${
            featured
              ? 'bg-gradient-to-r from-blue-500 via-cyan-500 to-purple-500 text-white shadow-glow-cyan'
              : 'bg-gradient-to-r from-gray-700 to-gray-800 dark:from-gray-600 dark:to-gray-700 text-white border border-gray-500/40'
          }`}>
            {annotation.label}
          </span>
        </div>
      )}

      {/* Size meter + savings */}
      <div className="relative flex items-center justify-between h-5">
        <PackMeter level={level} total={total} featured={featured} />
        {savings > 0 && (
          <span className={`px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wide tabular-nums ${
            featured
              ? 'bg-cyan-500/15 text-cyan-600 dark:text-cyan-300 ring-1 ring-cyan-400/30'
              : 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 ring-1 ring-emerald-500/20'
          }`}>
            Save {savings}%
          </span>
        )}
      </div>

      {/* Credits */}
      <div className="relative mt-5">
        <p className={`text-2xl md:text-3xl font-extrabold tracking-tight tabular-nums whitespace-nowrap leading-none ${
          featured
            ? 'bg-gradient-to-r from-blue-500 via-cyan-500 to-purple-500 bg-clip-text text-transparent'
            : 'text-gray-900 dark:text-white'
        }`}>
          {formatNum(topUp.credits)}
        </p>
        <p className="mt-2 text-[11px] font-semibold uppercase tracking-widest text-gray-400 dark:text-gray-500">
          cloud credits
        </p>
      </div>

      {/* Price */}
      <div className={`relative mt-5 pt-5 border-t ${
        featured ? 'border-cyan-400/20' : 'border-gray-100 dark:border-white/[0.06]'
      }`}>
        <div className="flex items-baseline gap-1.5">
          <span className="text-2xl font-bold tracking-tight tabular-nums text-gray-900 dark:text-white">
            ${formatNum(topUp.price)}
          </span>
          <span className="text-xs text-gray-400 dark:text-gray-500">one-time</span>
        </div>
        {rate != null && (
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400 tabular-nums">
            ${rate.toFixed(2)} per 1,000 credits
          </p>
        )}
      </div>

      {/* CTA */}
      <button
        type="button"
        onClick={() => onBuyCredits(topUp.stripe_price_id)}
        disabled={isDisabled}
        title={tooltip}
        aria-label={`Buy ${formatNum(topUp.credits)} credits for $${formatNum(topUp.price)}`}
        className={`relative z-10 mt-5 w-full py-3 rounded-xl text-sm font-bold transition-all duration-300 transform ${
          isLoading
            ? 'cursor-wait'
            : 'disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:scale-100 disabled:hover:shadow-none'
        } ${
          featured
            ? 'bg-gradient-to-r from-blue-500 via-cyan-500 to-purple-500 hover:from-blue-600 hover:via-cyan-600 hover:to-purple-600 text-white shadow-lg shadow-cyan-500/20 hover:shadow-glow-md hover:scale-[1.02]'
            : 'bg-gray-900 dark:bg-white hover:bg-gray-800 dark:hover:bg-gray-100 text-white dark:text-gray-900 hover:scale-[1.02] hover:shadow-md'
        }`}
      >
        {isLoading ? (
          <span className="flex items-center justify-center gap-2">
            <span className={`w-4 h-4 rounded-full border-2 animate-spin ${
              featured
                ? 'border-white/30 border-t-white'
                : 'border-white/30 border-t-white dark:border-gray-900/30 dark:border-t-gray-900'
            }`} />
            Processing...
          </span>
        ) : (
          <span className="flex items-center justify-center gap-2">
            Buy now
            <HiOutlineArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
          </span>
        )}
      </button>

      {/* Balance preview — only where the page knows the balance */}
      {newBalance != null && (
        <p className="relative mt-3 text-center text-[11px] text-gray-400 dark:text-gray-500 tabular-nums">
          New balance:{' '}
          <span className="font-semibold text-gray-600 dark:text-gray-300">{formatNum(newBalance)}</span>
        </p>
      )}
    </div>
  );
};

const CreditTopUps = ({
  creditTopUps,
  currentPlan,
  onBuyCredits,
  actionLoading,
  disabled = false,
  disabledTooltip,
  currentBalance = null,
}) => {
  const navigate = useNavigate();
  const location = useLocation();

  if (!creditTopUps.length) return null;

  const packs = [...creditTopUps].sort((a, b) => a.credits - b.credits);
  // The worst rate (the smallest pack) anchors every "Save X%" pill.
  const baseRate = Math.max(...packs.map((p) => ratePerThousand(p) ?? 0));
  const isFreePlan = currentPlan === 'free';
  const onPlansPage = PLANS_PATHS.includes(location.pathname);
  const gridCols = packs.length >= 4 ? 'lg:grid-cols-4' : 'lg:grid-cols-3';

  return (
    <section className="relative overflow-hidden rounded-2xl border border-gray-200 dark:border-gray-700/30 bg-gradient-to-br from-gray-50 dark:from-gray-900/60 to-gray-100 dark:to-gray-800/40 p-6 sm:p-8">
      {/* Top accent + ambient glow */}
      <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-cyan-400/50 to-transparent" />
      <div className="pointer-events-none absolute -top-28 right-0 w-80 h-80 rounded-full bg-cyan-400/[0.07] dark:bg-cyan-400/[0.05] blur-3xl" />
      <div className="pointer-events-none absolute -bottom-32 -left-16 w-72 h-72 rounded-full bg-purple-500/[0.05] dark:bg-purple-500/[0.05] blur-3xl" />

      {/* Header */}
      <div className="relative flex flex-col md:flex-row md:items-end md:justify-between gap-5 mb-8">
        <div>
          <p className="text-[11px] font-bold uppercase tracking-[0.2em] bg-gradient-to-r from-blue-500 via-cyan-500 to-purple-500 bg-clip-text text-transparent mb-2">
            Pay as you go
          </p>
          <h3 className="text-2xl md:text-[28px] font-extrabold tracking-tight text-gray-900 dark:text-white leading-tight">
            Credit Top-Ups
          </h3>
          <p className="mt-2 text-sm text-gray-500 dark:text-gray-400 max-w-xl leading-relaxed">
            Buy extra credits whenever you need them. One-time payment, and your plan stays exactly as it is.
          </p>
        </div>
        <div className="flex flex-wrap gap-2 md:justify-end md:max-w-xs">
          {TRUST_CHIPS.map(({ icon: Icon, text }) => (
            <span
              key={text}
              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium text-gray-600 dark:text-gray-300 bg-white/80 dark:bg-white/[0.04] border border-gray-200 dark:border-white/[0.08] whitespace-nowrap"
            >
              <Icon className="w-3.5 h-3.5 text-emerald-500 dark:text-emerald-400" />
              {text}
            </span>
          ))}
        </div>
      </div>

      {/* Free plan gate */}
      {isFreePlan && (
        <div className="relative mb-6 flex flex-col sm:flex-row sm:items-center gap-3 rounded-xl border border-amber-200/80 dark:border-amber-400/20 bg-amber-50/80 dark:bg-amber-400/[0.06] px-4 py-3">
          <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-amber-100 dark:bg-amber-400/15 flex items-center justify-center">
            <HiOutlineLockClosed className="w-4 h-4 text-amber-600 dark:text-amber-300" />
          </div>
          <p className="flex-1 text-sm text-amber-900 dark:text-amber-100/90">
            <span className="font-semibold">Top-ups are available on paid plans.</span>{' '}
            {onPlansPage
              ? 'Pick a plan above to unlock credit packs.'
              : 'Subscribe to unlock credit packs and the cloud platform.'}
          </p>
          {!onPlansPage && (
            <button
              type="button"
              onClick={() => navigate('/plans')}
              className="flex-shrink-0 inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg text-xs font-bold text-white bg-gradient-to-r from-blue-500 via-cyan-500 to-purple-500 hover:from-blue-600 hover:via-cyan-600 hover:to-purple-600 shadow-sm shadow-cyan-500/20 hover:shadow-cyan-500/30 transition-all"
            >
              See plans
              <HiOutlineArrowRight className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      )}

      {/* Packs */}
      <div className={`relative grid grid-cols-1 xs:grid-cols-2 ${gridCols} gap-4 pt-3`}>
        {packs.map((topUp, index) => (
          <TopUpCard
            key={topUp.credits}
            topUp={topUp}
            level={index + 1}
            total={packs.length}
            baseRate={baseRate}
            currentBalance={currentBalance}
            isFreePlan={isFreePlan}
            disabled={disabled}
            disabledTooltip={disabledTooltip}
            actionLoading={actionLoading}
            onBuyCredits={onBuyCredits}
          />
        ))}
      </div>
    </section>
  );
};

export default CreditTopUps;
